import {
  getCallCenterBaseUrl,
  requestCallCenter,
  unwrapEnvelopeValue,
} from "./CallCenterApi";
import { getStoredAccessToken } from "../utils/authSession";
import { assertCallingEnabled, isCallingEnabled } from "../utils/orgFeatures";
import { cachedRequest } from "./callCenterRequestCache";

export const createManualCall = async (payload) => {
  assertCallingEnabled();
  const envelope = await requestCallCenter({
    method: "post",
    url: "calls/manual",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to create manual call");
};

export const triggerAICall = async (payload) => {
  assertCallingEnabled();
  const envelope = await requestCallCenter({
    method: "post",
    url: "calls/ai/trigger",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to trigger AI call");
};

export const getCallById = async (callId) => {
  assertCallingEnabled();
  const envelope = await requestCallCenter({
    method: "get",
    url: `calls/${callId}`,
  });
  return unwrapEnvelopeValue(envelope, "Failed to fetch call details");
};

/**
 * @param {string} callId
 * @param {import("./CallCenterApi").ManualCallStatusUpdateRequest} payload
 */
export const updateCallManualStatus = async (callId, payload) => {
  assertCallingEnabled();
  const envelope = await requestCallCenter({
    method: "patch",
    url: `calls/${callId}/manual-status`,
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to update call status");
};

/**
 * Preferred manual-tracking endpoint for frontend.
 * Backend resolves the target call by JWT user context + accountNo.
 * @param {import("./CallCenterApi").ManualCallStatusByAccountRequest} payload
 */
export const updateManualStatusByAccount = async (payload) => {
  assertCallingEnabled();
  const envelope = await requestCallCenter({
    method: "patch",
    url: "calls/manual-status",
    data: payload,
  });
  return unwrapEnvelopeValue(envelope, "Failed to update call status");
};

export const getOfficeUsers = async () => {
  if (!isCallingEnabled()) {
    return [];
  }
  return cachedRequest("calls:office-users", async () => {
    const envelope = await requestCallCenter({
      method: "get",
      url: "calls/office-users",
    });
    return unwrapEnvelopeValue(envelope, "Failed to fetch office users");
  });
};

/**
 * SSE: native EventSource cannot send Authorization. Backend accepts JWT in the query
 * (access_token or token) — we use access_token, URL-encoded.
 * @param {string} callId
 * @returns {string}
 */
export const getCallStreamUrl = (callId) => {
  if (!callId || !isCallingEnabled()) {
    return null;
  }
  const path = `api/calls/${callId}/stream`;
  const base = `${getCallCenterBaseUrl()}${path}`;
  const token = getStoredAccessToken();
  if (!token) return base;
  const join = base.includes("?") ? "&" : "?";
  return `${base}${join}access_token=${encodeURIComponent(token)}`;
};
